"use client";

import { useState } from "react";
import type React from "react";
import Link from "next/link";
import { icons } from "lucide-react";
import type { Category } from "@/types";

// --- アイコン関連ユーティリティ ---

function FolderIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 7a2 2 0 012-2h3.586a1 1 0 01.707.293L10.707 6.7A1 1 0 0011.414 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
      />
    </svg>
  );
}

function toPascalCase(str: string): string {
  return str
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

function CategoryIcon({
  iconName,
  className,
  style,
}: {
  iconName?: string | null;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (iconName) {
    const pascalName = toPascalCase(iconName);
    const iconsMap: Record<
      string,
      React.ComponentType<{ className?: string; style?: React.CSSProperties }>
    > = icons;
    const IconComponent = iconsMap[pascalName];
    if (IconComponent) {
      return <IconComponent className={className} style={style} aria-hidden="true" />;
    }
  }
  return <FolderIcon className={className} style={style} />;
}

interface CategoryTreeItemProps {
  category: Category;
  activeCategoryId?: string;
  depth: number;
  isTopPage?: boolean;
  onCategoryClick?: () => void;
}

function CategoryTreeItem({
  category,
  activeCategoryId,
  depth,
  isTopPage = false,
  onCategoryClick,
}: CategoryTreeItemProps) {
  const isActive = activeCategoryId === category.id;
  const hasChildren = category.children.length > 0;
  const isChildActive =
    hasChildren && isCategoryOrDescendantActive(category.children, activeCategoryId);
  const [isOpen, setIsOpen] = useState(isActive || isChildActive);

  const paddingLeft = depth * 12 + 12;
  const href = isTopPage ? `/?categoryId=${category.id}` : `/categories/${category.slug}`;

  return (
    <li>
      <div className="flex items-center">
        <Link
          href={href}
          onClick={onCategoryClick}
          className={`flex flex-1 items-center gap-1.5 truncate rounded-md py-1.5 text-sm transition-colors ${
            isActive
              ? "bg-[var(--theme-primary-light)] font-bold text-[var(--theme-primary)]"
              : "text-ar-gray-600 hover:bg-ar-gray-50"
          }`}
          style={{ paddingLeft: `${paddingLeft}px`, paddingRight: "8px" }}
        >
          <CategoryIcon
            iconName={category.iconName}
            style={{
              width: "16px",
              height: "16px",
              flexShrink: 0,
              color: isActive ? "var(--theme-primary)" : "#9CA3AF",
            }}
          />
          <span className="truncate">{category.name}</span>
          {category.totalArticleCount > 0 && (
            <span
              className="ml-1.5 shrink-0 text-[11px]"
              style={{ color: isActive ? "var(--theme-primary)" : "#9CA3AF" }}
            >
              {category.totalArticleCount}
            </span>
          )}
        </Link>
        {hasChildren && (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-ar-gray-400 transition-colors hover:bg-ar-gray-50 hover:text-ar-gray-600"
            aria-label={isOpen ? "折りたたむ" : "展開する"}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transition: "transform 0.2s",
                transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <polyline points="4 2 8 6 4 10" />
            </svg>
          </button>
        )}
      </div>
      {hasChildren && isOpen && (
        <ul>
          {category.children.map((child: Category) => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              activeCategoryId={activeCategoryId}
              depth={depth + 1}
              isTopPage={isTopPage}
              onCategoryClick={onCategoryClick}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function isCategoryOrDescendantActive(categories: Category[], activeCategoryId?: string): boolean {
  if (!activeCategoryId) return false;
  for (const cat of categories) {
    if (cat.id === activeCategoryId) return true;
    if (cat.children.length > 0 && isCategoryOrDescendantActive(cat.children, activeCategoryId)) {
      return true;
    }
  }
  return false;
}

interface CategoryTreeSidebarProps {
  categories: Category[];
  activeCategoryId?: string;
  isTopPage?: boolean;
}

export function CategoryTreeSidebarContent({
  categories,
  activeCategoryId,
  isTopPage = false,
  onCategoryClick,
}: CategoryTreeSidebarProps & { onCategoryClick?: () => void }) {
  if (categories.length === 0) return null;

  return (
    <nav aria-label="カテゴリ">
      <h2 className="mb-2 text-xs font-bold tracking-wide text-ar-gray-400">カテゴリ</h2>
      <ul className="space-y-0.5">
        <li>
          <Link
            href="/"
            onClick={onCategoryClick}
            className={`block rounded-md px-3 py-1.5 text-sm transition-colors ${
              !activeCategoryId
                ? "bg-[var(--theme-primary-light)] font-bold text-[var(--theme-primary)]"
                : "text-ar-gray-600 hover:bg-ar-gray-50"
            }`}
          >
            すべて
          </Link>
        </li>
        {categories.map((cat) => (
          <CategoryTreeItem
            key={cat.id}
            category={cat}
            activeCategoryId={activeCategoryId}
            depth={0}
            isTopPage={isTopPage}
            onCategoryClick={onCategoryClick}
          />
        ))}
      </ul>
    </nav>
  );
}

/** デスクトップ用固定サイドバー */
export function CategoryTreeSidebar({
  categories,
  activeCategoryId,
  isTopPage = false,
}: CategoryTreeSidebarProps) {
  return (
    <aside
      className="hidden w-[240px] shrink-0 lg:block"
      style={{
        position: "sticky",
        top: "88px",
        maxHeight: "calc(100vh - 112px)",
        overflowY: "auto",
      }}
    >
      <CategoryTreeSidebarContent
        categories={categories}
        activeCategoryId={activeCategoryId}
        isTopPage={isTopPage}
      />
    </aside>
  );
}

/** モバイル用ドロワーサイドバー */
export function CategoryTreeDrawer({
  categories,
  activeCategoryId,
  isTopPage = false,
}: CategoryTreeSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="lg:hidden">
      {/* ドロワー開くボタン */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-ar-gray-600 transition-colors hover:bg-ar-gray-50"
        aria-label="カテゴリを表示"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="15" y2="12" />
          <line x1="3" y1="18" x2="9" y2="18" />
        </svg>
        カテゴリ
      </button>

      {/* オーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ドロワー本体 */}
      <div
        className="fixed inset-y-0 left-0 z-50 w-[280px] overflow-y-auto bg-white shadow-xl"
        style={{
          transition: "transform 0.3s ease",
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
        }}
        aria-hidden={!isOpen}
      >
        <div className="flex items-center justify-between border-b border-[#E5E7EB] px-4 py-3">
          <span className="text-sm font-semibold text-[#111827]">カテゴリ</span>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded text-ar-gray-400 transition-colors hover:bg-ar-gray-50 hover:text-ar-gray-600"
            aria-label="閉じる"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          <CategoryTreeSidebarContent
            categories={categories}
            activeCategoryId={activeCategoryId}
            isTopPage={isTopPage}
            onCategoryClick={() => setIsOpen(false)}
          />
        </div>
      </div>
    </div>
  );
}
